<template>
  <div class="reveal overwrite-page">
    <div class="slides">
      <section
        v-for="(diapositiva, index) of diapositivas"
        :class="{
          'slide--title': 0 === index,
          'slide--media': index !== 0,
        }"
        class="slide"
      >
        <img
          v-if="!diapositiva.PathFoto && !diapositiva.PathVideo"
          class="slide-logo"
          src="/images/terrassa-logo.png"
        />
        <img
          v-if="diapositiva.PathFoto && diapositiva.ContentType !== 'video'"
          class="slide-image"
          :src="diapositiva.PathFoto"
        />
        <video
          v-if="diapositiva.ContentType === 'video' && diapositiva.PathVideo"
          class="slide-video"
          autoplay
          muted
          playsinline
        >
          <source :src="diapositiva.PathVideo" />
        </video>
        <div class="text">
          <div class="missatge">
            {{
              diapositiva.Missatge.length > 200
                ? diapositiva.Missatge.slice(0, 200)
                : diapositiva.Missatge
            }}
          </div>
          <div v-if="diapositiva.Signatura" class="signatura">
            {{ diapositiva.Signatura }}
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  watch,
  nextTick,
  ref,
} from "vue";
import Reveal from "reveal.js";
import { Diapositiva } from "../types/vetlla.ts";

export default defineComponent({
  props: {
    diapositivas: {
      required: true,
      type: Array as PropType<Diapositiva[]>,
    },
    idVetlla: {
      required: true,
      type: Number,
    },
  },
  setup(props) {
    const reveal = ref<Reveal.Api | null>(null);
    const state = { initialized: false, initializing: false, lastLength: 0 };

    function resumeAutoSlide() {
      (reveal.value as any)?.resumeAutoSlide?.();
    }

    function pauseAutoSlide() {
      (reveal.value as any)?.pauseAutoSlide?.();
    }

    function resync() {
      reveal.value?.sync();
      resumeAutoSlide();
      reveal.value?.slide(0);
    }

    function onSlideChanged(event: any) {
      const video = event.currentSlide.querySelector("video");
      if (video) {
        pauseAutoSlide();
        video.play();
        video.onended = () => {
          resumeAutoSlide();
          reveal.value?.next();
        };
      } else {
        resumeAutoSlide();
      }
    }

    async function initReveal() {
      if (state.initializing || state.initialized) return;
      state.initializing = true;
      try {
        await nextTick();
        reveal.value = new Reveal();
        await reveal.value.initialize({
          controls: false,
          progress: true,
          autoSlide: 30000,
          autoSlideStoppable: false,
          loop: true,
          disableLayout: true,
        });
        reveal.value.on("slidechanged", onSlideChanged);
        state.initialized = true;
        resumeAutoSlide();
      } finally {
        state.initializing = false;
      }
    }

    onMounted(() => {
      if (props.diapositivas.length > 0) {
        initReveal();
      }
    });

    onBeforeUnmount(() => {
      reveal.value?.destroy();
    });

    watch(
      () => props.idVetlla,
      async () => {
        if (state.initialized) {
          await nextTick();
          resync();
        }
      },
    );

    watch(
      () => props.diapositivas,
      async (newVal) => {
        await nextTick();
        if (newVal.length === state.lastLength) return;
        state.lastLength = newVal.length;
        if (state.initialized) {
          resync();
        } else {
          initReveal();
        }
      },
      { deep: true },
    );

    return {};
  },
});
</script>

<style lang="scss">
@import "reveal.js/dist/reveal.css";
@import "reveal.js/dist/theme/black.css";

.overwrite-page {
  background: linear-gradient(to bottom, #5a3780, #9769e8);
}

.reveal .slides > section.present,
.reveal .slides > section > section.present {
  min-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  // position: absolute !important;
  // bottom: 0 !important;
}

.slide {
  max-height: 100%;
  height: 100%;
}

.slide-logo {
  height: 2em;
  position: absolute;
  top: 1em;
  left: 1em;
}

.slide-image {
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.slide-video {
  height: 100%;
  width: 100%;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.text {
  min-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;

  color: #ffffff;
  font-size: 1.6em;

  font-family: "Playfair Display", serif;
  font-optical-sizing: auto;
  font-weight: bold;
  font-style: italic;
}

.slide--title .missatge {
  position: absolute;
  bottom: 50%;
  transform: translateY(50%);
}

.slide--title .signatura {
  position: absolute;
  bottom: 1em;
  right: 1em;
}

.slide--media {
  .text {
    text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.65);
  }
  .missatge {
    position: absolute;
    bottom: 2em;
  }
  .signatura {
    position: absolute;
    bottom: 0.3em;
    right: 1em;
    font-size: 0.8em;
  }
}
</style>
